// src/pages/RegistroDDTIngressoPage.js
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useOrg } from "../contexts/OrgContext";
import { Search, FileText, Image, ChevronDown, ChevronUp } from "lucide-react";
import DDTIngressoTab from "../components/DDTIngressoTab";

const STATI = {
  ricevuto:   { label: "Ricevuto",   bg: "#dbeafe", col: "#1d4ed8" },
  verificato: { label: "Verificato", bg: "#d1fae5", col: "#065f46" },
  contestato: { label: "Contestato", bg: "#fee2e2", col: "#991b1b" },
};

export default function RegistroDDTIngressoPage({ onNavigate }) {
  const { effectiveOrgId } = useOrg();
  const [tutti, setTutti] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [cerca, setCerca] = useState("");
  const [filtroStato, setFiltroStato] = useState("");
  const [filtroDataDa, setFiltroDataDa] = useState("");
  const [filtroDataA, setFiltroDataA] = useState("");
  const [raggruppamento, setRaggruppamento] = useState("commessa"); // "commessa" | "data" | "mittente"
  const [aperte, setAperte] = useState({});
  const [showNuovo, setShowNuovo] = useState(false);
  const [editDDT, setEditDDT] = useState(null);

  useEffect(() => {
    if (!effectiveOrgId) return;
    return onSnapshot(
      query(collection(db, "ddtIngresso"), where("orgId", "==", effectiveOrgId)),
      snap => setTutti(snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => b.dataRicezione?.localeCompare(a.dataRicezione || "") || 0))
    );
  }, [effectiveOrgId]);

  useEffect(() => {
    if (!effectiveOrgId) return;
    onSnapshot(
      query(collection(db, "projects"), where("organizationId", "==", effectiveOrgId), where("archived", "==", false), where("statoCommessa", "==", "attiva")),
      snap => setCommesse(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => !p.isParent))
    );
  }, [effectiveOrgId]);

  const filtrati = tutti.filter(d => {
    if (filtroStato && d.stato !== filtroStato) return false;
    if (filtroDataDa && d.dataRicezione < filtroDataDa) return false;
    if (filtroDataA && d.dataRicezione > filtroDataA) return false;
    if (cerca) {
      const s = cerca.toLowerCase();
      return (d.mittente || "").toLowerCase().includes(s) ||
        (d.descrizione || "").toLowerCase().includes(s) ||
        (d.nDocumento || "").toLowerCase().includes(s) ||
        (d.projectName || "").toLowerCase().includes(s);
    }
    return true;
  });

  // Raggruppa
  const gruppi = filtrati.reduce((acc, d) => {
    let chiave;
    if (raggruppamento === "commessa") chiave = d.projectName || "Senza commessa";
    else if (raggruppamento === "mittente") chiave = d.mittente || "Mittente sconosciuto";
    else chiave = d.dataRicezione ? d.dataRicezione.slice(0, 7) : "—"; // YYYY-MM
    if (!acc[chiave]) acc[chiave] = [];
    acc[chiave].push(d);
    return acc;
  }, {});

  const toggleGruppo = k => setAperte(a => ({ ...a, [k]: !a[k] }));

  if (showNuovo || editDDT) {
    const progetto = commesse.find(c => c.id === editDDT?.projectId);
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
        <DDTIngressoTab
          projectId={editDDT?.projectId || null}
          projectName={editDDT?.projectName || progetto?.name || ""}
          codiceCommessa={editDDT?.codiceCommessa || progetto?.codiceCommessa || ""}
          orgId={effectiveOrgId}
          _editDDT={editDDT}
          _onClose={() => { setShowNuovo(false); setEditDDT(null); }}
        />
      </div>
    );
  }

  const inp = { padding: "6px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12, fontFamily: "'Inter',sans-serif", outline: "none" };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "16px 24px", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: "#1e1b4b", margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
            📥 DDT in ingresso
          </h2>
          <span style={{ fontSize: 12, color: "#9ca3af" }}>{filtrati.length} documento/i</span>
        </div>
        {/* Filtri */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ position: "relative" }}>
            <Search size={13} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }} />
            <input value={cerca} onChange={e => setCerca(e.target.value)} placeholder="Cerca mittente, descrizione..."
              style={{ ...inp, paddingLeft: 26, width: 220 }} />
          </div>
          <button onClick={() => setFiltroStato(s => s === "ricevuto" ? "" : "ricevuto")}
            style={{padding:"6px 12px",border:"1px solid "+(filtroStato==="ricevuto"?"#1d4ed8":"#e5e7eb"),borderRadius:8,
              background:filtroStato==="ricevuto"?"#dbeafe":"#fff",color:filtroStato==="ricevuto"?"#1d4ed8":"#374151",
              fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>
            🔍 Solo da verificare
          </button>
          <button onClick={() => setFiltroStato(s => s === "contestato" ? "" : "contestato")}
            style={{padding:"6px 12px",border:"1px solid "+(filtroStato==="contestato"?"#991b1b":"#e5e7eb"),borderRadius:8,
              background:filtroStato==="contestato"?"#fee2e2":"#fff",color:filtroStato==="contestato"?"#991b1b":"#374151",
              fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>
            ⚠️ Solo contestati
          </button>
          <select value={filtroStato} onChange={e => setFiltroStato(e.target.value)} style={inp}>
            <option value="">Tutti gli stati</option>
            {Object.entries(STATI).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <input type="date" value={filtroDataDa} onChange={e => setFiltroDataDa(e.target.value)} style={inp} title="Da data" />
          <input type="date" value={filtroDataA} onChange={e => setFiltroDataA(e.target.value)} style={inp} title="A data" />
          <select value={raggruppamento} onChange={e => setRaggruppamento(e.target.value)} style={{ ...inp, fontWeight: 600 }}>
            <option value="commessa">Raggruppa per commessa</option>
            <option value="mittente">Raggruppa per mittente</option>
            <option value="data">Raggruppa per mese</option>
          </select>
          {(cerca || filtroStato || filtroDataDa || filtroDataA) && (
            <button onClick={() => { setCerca(""); setFiltroStato(""); setFiltroDataDa(""); setFiltroDataA(""); }}
              style={{ padding: "6px 12px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", fontSize: 12, cursor: "pointer" }}>
              × Reset
            </button>
          )}
        </div>
      </div>

      {/* Lista raggruppata */}
      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px" }}>
        {Object.keys(gruppi).length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>
            <div style={{ fontSize: 48, marginBottom: 12, opacity: .15 }}>📥</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Nessun DDT in ingresso</div>
          </div>
        ) : (
          Object.entries(gruppi).sort(([a], [b]) => a.localeCompare(b)).map(([gruppo, docs]) => {
            const isAperta = aperte[gruppo] !== false; // default aperto
            return (
              <div key={gruppo} style={{ marginBottom: 12 }}>
                {/* Header gruppo */}
                <div onClick={() => toggleGruppo(gruppo)}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: isAperta ? "8px 8px 0 0" : 8, cursor: "pointer", userSelect: "none" }}>
                  {isAperta ? <ChevronDown size={14} color="#6b7280" /> : <ChevronUp size={14} color="#6b7280" />}
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#1e1b4b", flex: 1 }}>{gruppo}</span>
                  <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, background: "#e5e7eb", color: "#6b7280", fontWeight: 600 }}>{docs.length} doc.</span>
                </div>

                {/* Righe gruppo */}
                {isAperta && (
                  <div style={{ border: "1px solid #e5e7eb", borderTop: "none", borderRadius: "0 0 8px 8px", overflow: "hidden" }}>
                    {docs.map((d, i) => {
                      const stato = STATI[d.stato] || STATI.ricevuto;
                      return (
                        <div key={d.id} onClick={() => setEditDDT(d)}
                          style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: i % 2 === 0 ? "#fff" : "#fafafa", borderBottom: i < docs.length - 1 ? "1px solid #f3f4f6" : "none", cursor: "pointer" }}>
                          {/* Thumb */}
                          {d.allegati?.[0]?.tipo === "foto" ? (
                            <img src={d.allegati[0].url} alt="" style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4, flexShrink: 0 }} />
                          ) : d.allegati?.[0]?.tipo === "pdf" ? (
                            <div style={{ width: 40, height: 40, borderRadius: 4, background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              <FileText size={18} color="#dc2626" />
                            </div>
                          ) : (
                            <div style={{ width: 40, height: 40, borderRadius: 4, background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              <Image size={18} color="#9ca3af" />
                            </div>
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: "#1e1b4b" }}>{d.mittente}</span>
                              {d.nDocumento && <span style={{ fontSize: 11, fontFamily: "monospace", color: "#7c3aed" }}>#{d.nDocumento}</span>}
                            </div>
                            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 1 }}>{d.descrizione?.slice(0, 80)}{d.descrizione?.length > 80 ? "..." : ""}</div>
                          </div>
                          <div style={{ textAlign: "right", flexShrink: 0 }}>
                            <div style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, background: stato.bg, color: stato.col, fontWeight: 700, marginBottom: 2 }}>{stato.label}</div>
                            <div style={{ fontSize: 10, color: "#9ca3af" }}>
                              {d.dataRicezione ? new Date(d.dataRicezione + "T00:00:00").toLocaleDateString("it-IT") : "—"}
                            </div>
                            {d.allegati?.length > 0 && <div style={{ fontSize: 10, color: "#d97706", fontWeight: 600 }}>📎 {d.allegati.length}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
